import {
  applyPreferredDevice,
  clearPreferredDeviceId,
  getPreferredDeviceId,
  removePreferredDeviceId,
  removePreferredDeviceIds
} from '../utils/mediaDevices';

const CAPTURE_TIMEOUT_MS = 20000;
const audioConstraints = { echoCancellation: true, noiseSuppression: true };
const stop = stream => stream && stream.getTracks().forEach(track => track.stop());
const DEVICE_FALLBACK_ERRORS = new Set(['NotFoundError', 'OverconstrainedError', 'NotReadableError']);

async function getUserMediaWithDeviceFallback(constraints) {
  const preferredKinds = ['video', 'audio'].filter(kind => getPreferredDeviceId(kind));
  try {
    return await navigator.mediaDevices.getUserMedia(constraints);
  } catch (error) {
    if (!preferredKinds.length || !DEVICE_FALLBACK_ERRORS.has(error && error.name)) throw error;
  }

  for (const kind of preferredKinds) {
    try {
      const stream = await navigator.mediaDevices.getUserMedia(removePreferredDeviceId(constraints, kind));
      clearPreferredDeviceId(kind);
      return stream;
    } catch (error) {
      if (!DEVICE_FALLBACK_ERRORS.has(error && error.name)) throw error;
    }
  }

  const stream = await navigator.mediaDevices.getUserMedia(removePreferredDeviceIds(constraints));
  preferredKinds.forEach(kind => clearPreferredDeviceId(kind));
  return stream;
}

export function captureError(error, kind) {
  const name = error && error.name || 'CaptureError';
  const device = kind === 'video' ? '摄像头或麦克风' : kind === 'screen' ? '共享屏幕' : '麦克风';
  const messages = {
    NotFoundError: `未检测到${device}，请确认设备已连接并被系统识别`,
    NotAllowedError: `未获得${device}权限，请在系统隐私设置中允许访问`,
    NotReadableError: `${device}无法读取，请检查是否被其他应用占用`,
    OverconstrainedError: `${device}不支持当前采集参数`,
    TimeoutError: `${device}启动超时，请检查权限或设备后重试`,
    AbortError: '已取消设备采集'
  };
  const result = new Error(messages[name] || `${device}启动失败，请检查设备和权限`);
  result.name = name;
  return result;
}

class ImCamera {
  constructor() {
    this.stream = null;
    this.generation = 0;
    this.cancelPending = null;
  }

  isEnable() {
    return typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;
  }

  acquire(kind, request) {
    this.close();
    const generation = this.generation;
    return new Promise((resolve, reject) => {
      let finished = false;
      const finishError = error => {
        if (finished) return;
        finished = true;
        if (generation === this.generation) this.generation++;
        clearTimeout(timer);
        this.cancelPending = null;
        stop(this.pendingScreen);
        this.pendingScreen = null;
        reject(captureError(error, kind));
      };
      const timer = setTimeout(() => finishError({ name: 'TimeoutError' }), CAPTURE_TIMEOUT_MS);
      this.cancelPending = () => finishError({ name: 'AbortError' });
      Promise.resolve().then(() => finished ? null : request()).then(stream => {
        if (finished || generation !== this.generation) {
          stop(stream);
          return;
        }
        finished = true;
        clearTimeout(timer);
        this.cancelPending = null;
        this.stream = stream;
        resolve(stream);
      }).catch(finishError);
    });
  }

  openVideo() {
    return this.acquire('video', () => getUserMediaWithDeviceFallback({
      video: applyPreferredDevice('video', true),
      audio: applyPreferredDevice('audio', audioConstraints)
    }));
  }

  openAudio() {
    return this.acquire('audio', () => getUserMediaWithDeviceFallback({
      video: false,
      audio: applyPreferredDevice('audio', audioConstraints)
    }));
  }

  openVideoOnly() {
    return this.acquire('video', () => getUserMediaWithDeviceFallback({
      video: applyPreferredDevice('video', true),
      audio: false
    }));
  }

  openScreen(withAudio) {
    return this.acquire('screen', async () => {
      const generation = this.generation;
      const screen = await navigator.mediaDevices.getDisplayMedia({ video: true });
      if (generation !== this.generation) {
        stop(screen);
        throw { name: 'AbortError' };
      }
      this.pendingScreen = screen;
      try {
        if (withAudio) {
          const audio = await navigator.mediaDevices.getUserMedia({ video: false, audio: audioConstraints });
          if (generation !== this.generation) {
            stop(audio);
            throw { name: 'AbortError' };
          }
          audio.getAudioTracks().forEach(track => screen.addTrack(track));
        }
        if (this.pendingScreen === screen) this.pendingScreen = null;
        return screen;
      } catch (error) {
        stop(screen);
        throw error;
      }
    });
  }

  stopStream() {
    this.close();
  }

  close() {
    this.generation++;
    if (this.cancelPending) this.cancelPending();
    stop(this.pendingScreen);
    this.pendingScreen = null;
    stop(this.stream);
    this.stream = null;
  }
}

export default ImCamera;
